import { useState } from 'react';
import { api, ago, useApi, type ApiFailure } from '../lib/api.ts';
import { Empty, ErrorState, Loading, Status, can, orgPath, useOrg, useToast } from '../lib/ui.tsx';

interface Alert { id: string; kind: string; severity: string; status: string; screenName: string | null; locationName: string | null; detail: Record<string, unknown>; occurrences: number; firstSeenAt: string; lastSeenAt: string; resolution: string | null }

const EXPLAIN: Record<string, string> = {
  device_offline: 'No heartbeat for over 10 minutes. The screen may still be showing cached content.',
  heartbeat_stale: 'Heartbeat is late (over 2 minutes).',
  disk_pressure: 'Player storage is nearly full; new releases may be blocked.',
  download_blocked: 'A release could not download because of storage.',
  playback_degraded: 'The player reported degraded or recovering playback.',
  playback_stalled: 'A stream stalled and the player switched to fallback content.',
  corrupt_media: 'A download failed its checksum and was discarded.',
  clock_drift: 'The device clock differs from server time by more than 5 minutes.',
  old_player: 'The player app is older than the supported version.',
  deployment_failed: 'A publication failed or only partly deployed.',
  transcode_backlog: 'Media has been waiting for processing for over 30 minutes.',
  rights_expiring: 'Content rights expire within 7 days.',
};

export function Alerts() {
  const org = useOrg();
  const toast = useToast();
  const [status, setStatus] = useState('open');
  const { data, error, loading, reload } = useApi<{ alerts: Alert[] }>(orgPath(org, `/alerts?status=${status}`), [org.id, status]);
  async function act(a: Alert, action: 'acknowledge' | 'resolve') {
    try {
      await api('POST', orgPath(org, `/alerts/${a.id}/${action}`), action === 'resolve' ? { resolution: 'Resolved by operator' } : {});
      toast(action === 'resolve' ? 'Resolved' : 'Acknowledged');
      void reload();
    } catch (e) {
      toast((e as ApiFailure).message, 'danger');
    }
  }
  return (
    <>
      <div className="page-head">
        <div><h1>Alerts</h1><p>Deduplicated fleet health. Repeated occurrences increase the count instead of creating noise.</p></div>
        <div className="segmented" role="group" aria-label="Status">
          {['open', 'acknowledged', 'resolved', 'all'].map((s) => <button key={s} aria-pressed={status === s} onClick={() => setStatus(s)}>{s}</button>)}
        </div>
      </div>
      {error && <ErrorState error={error} retry={reload} />}
      {loading && !data && <Loading />}
      {data && !data.alerts.length && <div className="card"><Empty title={status === 'open' ? 'All clear' : 'No alerts'}>Nothing needs attention right now.</Empty></div>}
      {data && !!data.alerts.length && (
        <div className="card table-wrap">
          <table className="table">
            <thead><tr><th>Alert</th><th>Severity</th><th>Where</th><th>Seen</th><th>Status</th><th /></tr></thead>
            <tbody>
              {data.alerts.map((a) => (
                <tr key={a.id}>
                  <td><strong>{a.kind.replace(/_/g, ' ')}</strong><div className="subtle">{EXPLAIN[a.kind] ?? ''}</div></td>
                  <td><Status value={a.severity} /></td>
                  <td>{a.screenName ?? '—'}<div className="subtle">{a.locationName}</div></td>
                  <td className="subtle">{a.occurrences}× · last {ago(a.lastSeenAt)}</td>
                  <td><Status value={a.status} />{a.resolution && <div className="subtle">{a.resolution}</div>}</td>
                  <td className="row">
                    {can(org, 'screens.manage') && a.status === 'open' && <button className="btn btn-sm" onClick={() => void act(a, 'acknowledge')}>Acknowledge</button>}
                    {can(org, 'screens.manage') && a.status !== 'resolved' && <button className="btn btn-sm" onClick={() => void act(a, 'resolve')}>Resolve</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
